import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import PageHeader from "../components/PageHeader";
import StatChip from "../components/StatChip";
import Loader from "../components/Loader";
import DateRangeFilter from "../components/DateRangeFilter";
import ReportViewer from "../components/ReportViewer";
import ReportTypeMenu from "../components/ReportTypeMenu";
import FilterPanel from "../components/FilterPanel";
import { matchesAdvancedFilters } from "../utils/filterUtils";
import {
  durationHHMM,
  formatDateTimeDisplay,
  isActiveUser,
  lastSevenDays,
} from "../utils/common";

import { getPunchingReport } from "../redux/dashboard/dashboardActions";
import { usersApis } from "../redux/users/userActions";
import { selectUsers } from "../redux/users/userSelectors";
import { useTabRefresh } from "../context/TabsContext";

/* How the sheet is grouped. Person and day are the two ways an attendance
   report is ever read — "how was this week for Deepak" and "who was in on
   Tuesday" — and each grouping brings its own sub-totals with it. */
const REPORT_TYPES = [
  { key: "none", label: "Detailed (No Grouping)", icon: "bi-list-ul", tone: "grey" },
  { key: "UserName", label: "User Wise", icon: "bi-person-badge", tone: "purple" },
  { key: "PunchDay", label: "Date Wise", icon: "bi-calendar3", tone: "orange" },
];

/**
 * Punching Report — attendance, with the away time broken out.
 *
 * The dashboard card has one "Break Hours" figure, which cannot tell an hour
 * of lunch from six five-minute breaks. This is the sheet that can: Break,
 * Lunch and Be right back each get a column, and they add up alongside worked
 * time rather than disappearing into it.
 *
 * Built on the same ReportViewer as the other two reports, so paper size, the
 * column picker, grouping, sub-totals, print and both exports come for free
 * and behave identically — an attendance sheet that exported differently from
 * the query reports would be a second thing to learn for no reason.
 */
const PunchingReport = () => {
  const dispatch = useDispatch();

  const [dateRange, setDateRange] = useState(lastSevenDays);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState("UserName");
  const [userFilter, setUserFilter] = useState("");
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterList, setFilterList] = useState([]);

  const usersData = useSelector(selectUsers);

  /* When these rows were read. Shown beside Reload, because the useful
     question is not "is there a button" but "is what I am looking at
     still true" — and an attendance report goes stale by the minute. */
  const [fetchedAt, setFetchedAt] = useState(null);

  /* Active accounts only.
   *
   * /users returns everybody, deactivated people included, because the Users
   * screen has to be able to turn one back on. Every other screen offers a
   * person as a CHOICE, and offering a deactivated one is offering a filter
   * that can only ever return an empty report. */
  const usersList = useMemo(() => {
    const d = usersData?.data;
    const all = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
    return all.filter(isActiveUser);
  }, [usersData]);

  useEffect(() => {
    dispatch(usersApis({ currentPage: 1, pageSize: 1000 }));
  }, [dispatch]);

  const toApiDay = (d) => {
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return undefined;
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  /* Pulled out of the effect so something other than a parameter change can
     ask for the rows again — the Reload button below, and coming back to this
     tab. Punches are written all day by people who are not looking at this
     screen, so a report opened at nine is a report about nine unless it is
     asked again. */
  const fetchRows = useCallback(() => {
    if (!dateRange.start || !dateRange.end) return;
    setLoading(true);
    dispatch(
      getPunchingReport({
        fromDate: toApiDay(dateRange.start),
        toDate: toApiDay(dateRange.end),
        ...(userFilter ? { userCode: userFilter } : {}),
      }),
    )
      .unwrap()
      .then((res) => {
        /* successHandler wraps once and axios wraps again, and which level
           carries the rows has differed by endpoint before. */
        const body = res?.data ?? res;
        const inner = body?.data ?? body;
        setRows(Array.isArray(inner) ? inner : []);
        setFetchedAt(new Date());
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, dateRange.start, dateRange.end, userFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  /* This pane stays mounted while another tab is on screen, so the effect
     above — keyed on the parameters — never fires again on its own. Without
     this, coming back to the tab shows whatever was last fetched, however
     old, with nothing saying so. */
  useTabRefresh(() => fetchRows());

  /* One derived field, added here rather than in SQL: the report groups by it,
     and a grouping key has to be the same string every time or each row
     becomes its own group. */
  const prepared = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        PunchDay: formatDateTimeDisplay(r.PunchDate, "DD/MM/YYYY", "(no date)"),
      })),
    [rows],
  );

  const columns = useMemo(
    () => [
      {
        key: "PunchDay",
        label: "Date",
        width: "82px",
        value: (r) => r.PunchDay,
      },
      { key: "UserName", label: "User", width: "104px", value: (r) => r.UserName },
      {
        key: "FirstIn",
        label: "First In",
        width: "74px",
        value: (r) => formatDateTimeDisplay(r.FirstIn, "hh:mm A", "--:--"),
      },
      {
        key: "LastOut",
        label: "Last Out",
        width: "74px",
        value: (r) => formatDateTimeDisplay(r.LastOut, "hh:mm A", "--:--"),
      },
      {
        key: "Punches",
        label: "Punches",
        width: "60px",
        total: true,
        value: (r) => r.Punches || 0,
      },
      /* Every duration below is MINUTES with kind:"duration". The viewer shows
         HH:MM and can still sub-total them; a "07:03" string could only be
         displayed, never added. */
      {
        key: "WorkedMinutes",
        label: "Worked",
        width: "70px",
        total: true,
        kind: "duration",
        value: (r) => r.WorkedMinutes || 0,
      },
      {
        key: "BreakMinutes",
        label: "Break",
        width: "64px",
        total: true,
        kind: "duration",
        value: (r) => r.BreakMinutes || 0,
      },
      {
        key: "LunchMinutes",
        label: "Lunch",
        width: "64px",
        total: true,
        kind: "duration",
        value: (r) => r.LunchMinutes || 0,
      },
      {
        key: "BrbMinutes",
        label: "Be Right Back",
        width: "76px",
        total: true,
        kind: "duration",
        value: (r) => r.BrbMinutes || 0,
      },
      {
        /* Time off the clock that nobody gave a reason for — a Log out, a
           device punch, the tab-close beacon. Its own column rather than
           folded into Break, which would overstate breaks. */
        key: "OtherAwayMinutes",
        label: "Other Away",
        width: "72px",
        total: true,
        kind: "duration",
        value: (r) => r.OtherAwayMinutes || 0,
      },
      {
        key: "AwayMinutes",
        label: "Total Away",
        width: "74px",
        total: true,
        kind: "duration",
        value: (r) => r.AwayMinutes || 0,
      },
    ],
    [],
  );

  /* What Advanced Filters offers, built from the report's own columns so the
     two can never list different things. */
  const filterColumns = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        type: c.kind === "duration" || c.key === "Punches" ? "number" : "text",
        value: c.value,
      })),
    [columns],
  );

  const filtered = useMemo(
    () =>
      filterList.length === 0
        ? prepared
        : prepared.filter((r) => matchesAdvancedFilters(r, filterList, filterColumns)),
    [prepared, filterList, filterColumns],
  );

  const totals = useMemo(
    () =>
      filtered.reduce(
        (acc, r) => ({
          worked: acc.worked + (r.WorkedMinutes || 0),
          away: acc.away + (r.AwayMinutes || 0),
        }),
        { worked: 0, away: 0 },
      ),
    [filtered],
  );

  const activeType = useMemo(
    () => REPORT_TYPES.find((t) => t.key === reportType) ?? REPORT_TYPES[0],
    [reportType],
  );

  return (
    <div className="report-container p-2 p-md-4">
      <PageHeader
        icon={<i className="bi bi-fingerprint"></i>}
        title="Punching Report"
        actions={
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <StatChip icon="bi-calendar-check" tone="blue" label="Rows" value={filtered.length} />
            <StatChip icon="bi-briefcase-fill" tone="green" label="Worked" value={durationHHMM(totals.worked)} />
            <StatChip icon="bi-cup-hot-fill" tone="orange" label="Away" value={durationHHMM(totals.away)} />

            <select
              className="form-select form-select-sm"
              style={{ width: "auto", minWidth: "150px", height: "38px" }}
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              title="One person, or everybody"
            >
              <option value="">All users</option>
              {usersList.map((u) => (
                <option key={u.UserCode} value={u.UserCode}>
                  {u.UserName}
                </option>
              ))}
            </select>

            <div style={{ width: "260px" }}>
              <DateRangeFilter
                startDate={dateRange.start}
                endDate={dateRange.end}
                onChange={(start, end) => setDateRange({ start, end })}
              />
            </div>

            <ReportTypeMenu types={REPORT_TYPES} value={reportType} onChange={setReportType} />

            {/* Re-reads the rows for the parameters already chosen. Not a page
                reload: this screen lives in a tab beside others that are
                holding filters, scroll positions and half-typed values, and
                reloading the window to refresh one report throws all of that
                away. */}
            <button
              className="btn btn-light border shadow-sm d-flex align-items-center gap-2"
              onClick={fetchRows}
              disabled={loading}
              title={
                fetchedAt
                  ? `Reload — last read at ${formatDateTimeDisplay(fetchedAt, "hh:mm:ss A")}`
                  : "Reload"
              }
            >
              <i
                className={`bi bi-arrow-clockwise${loading ? " sk-spin" : ""}`}
              />
              {fetchedAt && (
                <span
                  className="d-none d-lg-inline text-muted"
                  style={{ fontSize: "0.72rem", fontVariantNumeric: "tabular-nums" }}
                >
                  {formatDateTimeDisplay(fetchedAt, "hh:mm A")}
                </span>
              )}
            </button>

            <button
              className={`btn shadow-sm d-flex align-items-center gap-2 ${
                filterList.length ? "btn-primary" : "btn-light border"
              }`}
              onClick={() => setShowFilterPanel(true)}
              title="Advanced filters"
            >
              <i className={`bi ${filterList.length ? "bi-funnel-fill" : "bi-funnel"}`} />
              {filterList.length > 0 && (
                <span className="badge bg-white text-primary" style={{ fontSize: "0.7rem" }}>
                  {filterList.length}
                </span>
              )}
            </button>
          </div>
        }
      />

      {/* Rendered conditionally, not toggled by a prop: FilterPanel has no
          `show` of its own, and it owns the filter list through
          setFilterList rather than handing one back on apply. Guessing that
          API cost a white screen — `filterList` arrived undefined and the
          first .map threw. */}
      {showFilterPanel && (
        <FilterPanel
          filterList={filterList}
          setFilterList={setFilterList}
          onClose={() => setShowFilterPanel(false)}
          onClearAll={() => setFilterList([])}
          columns={filterColumns}
          data={prepared}
        />
      )}

      <div className="position-relative" style={{ minHeight: "300px" }}>
        {loading && <Loader isLoading />}

        <ReportViewer
          show
          embedded
          title="Punching Report"
          subtitle={activeType.key === "none" ? null : activeType.label}
          dateRange={{ startDate: dateRange.start, endDate: dateRange.end }}
          columns={columns}
          rows={filtered}
          groupBy={
            activeType.key === "none"
              ? null
              : {
                  key: activeType.key,
                  label: activeType.label.replace(/ Wise$/, ""),
                  value: (r) => r[activeType.key] || "(none)",
                }
          }
        />
      </div>
    </div>
  );
};

export default PunchingReport;
